/**
 * Active session time — how long this tab has actually been in front of you.
 *
 * "Active" means VISIBLE. The clock accumulates while the tab is visible and
 * stops while it is hidden, so a project left open in a background tab
 * overnight does not report fourteen hours of work. That was the semantic of
 * the original `_formatSessionDuration` in `ui/toolbar.js`, and it is the only
 * reading of "active session" that is not a lie.
 *
 * Pure and DOM-free, with `now` injectable: the browser wires
 * `visibilitychange` to `setVisible()`, and the tests drive the same object
 * with a fake clock. Nothing here owns a timer — a caller that wants to redraw
 * decides when to read, which is what keeps this out of the always-ticking
 * `setInterval` that got the original removed (see `ui/command-bar/save-pill.js`
 * and `docs/command-bar-contracts.md` §241).
 */

/**
 * @param {number} ms active milliseconds
 * @returns {string} e.g. 'under a minute', '42 min', '2h 05m'
 */
export function formatActiveDuration(ms) {
  const safe = Number.isFinite(ms) && ms > 0 ? ms : 0;
  const totalMinutes = Math.floor(safe / 60000);
  if (totalMinutes < 1) return 'under a minute';
  if (totalMinutes < 60) return `${totalMinutes} min`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}h ${String(minutes).padStart(2, '0')}m`;
}

/**
 * @param {object} [options]
 * @param {() => number} [options.now] clock source, injected for tests
 * @param {boolean} [options.visible] whether the tab starts visible
 */
export function createSessionClock({ now = () => Date.now(), visible = true } = {}) {
  let accumulatedMs = 0;
  let visibleSince = visible ? now() : null;

  return {
    /**
     * Idempotent on purpose: `visibilitychange` can fire repeatedly with the
     * same state, and banking the running span twice would double-count it.
     */
    setVisible(isVisible) {
      if (isVisible) {
        if (visibleSince === null) visibleSince = now();
        return;
      }
      if (visibleSince !== null) {
        accumulatedMs += Math.max(0, now() - visibleSince);
        visibleSince = null;
      }
    },

    /** @returns {number} active milliseconds so far */
    activeMs() {
      const running = visibleSince === null ? 0 : Math.max(0, now() - visibleSince);
      return accumulatedMs + running;
    },

    /** @returns {string} the formatted reading */
    text() {
      return formatActiveDuration(this.activeMs());
    }
  };
}
